import lang from '../lib/language';
import { LiquidityAndSolvencyRatiosResponse, LiquidityAndSolvencyRatiosResponseElement, OptionalYearAndPeriod, ProfitabilityRatiosResponse, ProfitabilityRatiosResponseElement, RatiosPeriodType, ValuationRatiosResponse, ValuationRatiosResponseElement } from '../lib/models/fundamental/types';

const emptyResult = '-';

const getTimeOption = (ratio: OptionalYearAndPeriod, period: RatiosPeriodType) => {
  if (period === RatiosPeriodType.ANNUAL) {
    return ratio.calendarYear?.toString();
  }
  return `${ratio.period?.toUpperCase()}'${ratio.calendarYear?.toString().slice(2)}`;
};

type profitabilityProps = {
  profitabilityStateData: ProfitabilityRatiosResponse;
  profitabilityPeriodType: RatiosPeriodType;
  setProfitabilityTimeOptions: (timeOptions: string[]) => void;
  profitabilityCurrentTimeOption: number;
}

export const getProfitabilityData = (props: profitabilityProps) => {
  const {
    profitabilityStateData,
    profitabilityPeriodType,
    setProfitabilityTimeOptions,
    profitabilityCurrentTimeOption,
  } = props;

  const isTtmPeriod = profitabilityPeriodType === RatiosPeriodType.TTM;
  const isAnnualPeriod = profitabilityPeriodType === RatiosPeriodType.ANNUAL;

  let timeOptions: string[] = [];
  let calendarYear: number | undefined;

  if (profitabilityStateData.ratios) {
    if (isAnnualPeriod) {
      const ratiosPeriods = new Set<string>(
        profitabilityStateData.ratios.map((ratio) => `${ratio.calendarYear}`),
      );

      let intersectedPeriods: string[];

      if (profitabilityStateData.sectorRatios) {
        const sectorRatiosPeriods = new Set<string>(
          profitabilityStateData.sectorRatios.map((sectorRatio) => `${sectorRatio.calendarYear}`),
        );

        intersectedPeriods = Array.from(ratiosPeriods).filter(period => sectorRatiosPeriods.has(period));
      } else {
        intersectedPeriods = Array.from(ratiosPeriods);
      }

      timeOptions = intersectedPeriods.map(period => {
        const [year] = period.split('-');
        return getTimeOption({ calendarYear: parseInt(year, 10) }, profitabilityPeriodType);
      }).filter((item): item is string => item !== undefined);

      const currentRatio = profitabilityStateData.ratios[profitabilityCurrentTimeOption];
      calendarYear = currentRatio?.calendarYear;
    } else {
      // Logic from the old function for non-annual periods
      timeOptions = profitabilityStateData.ratios
        .map((ratio) => getTimeOption(ratio, profitabilityPeriodType))
        .filter((item): item is string => item !== undefined);
    }
  }

  setProfitabilityTimeOptions(timeOptions);

  const company = isTtmPeriod
    ? profitabilityStateData.ttm
    : profitabilityStateData.ratios && profitabilityStateData.ratios[profitabilityCurrentTimeOption];

  let sector: ProfitabilityRatiosResponseElement | undefined;

  if (isTtmPeriod) {
    sector = profitabilityStateData.sectorTtm;
  } else if (isAnnualPeriod) {
    sector = profitabilityStateData.sectorRatios?.find(
      sectorRatio => sectorRatio.calendarYear === calendarYear,
    );
  } else if (profitabilityStateData.sectorRatios) {
    sector = profitabilityStateData.sectorRatios[profitabilityCurrentTimeOption];
  }

  const profitabilityNames = [
    lang.ratiosGrossMargin(),
    lang.ratiosOperatingMargin(),
    lang.ratiosNetProfitMargin(),
    lang.ratiosNetIncome(),
    lang.ratiosBookValuePerShare(),
    lang.ratiosReturnOnAssets(),
    lang.ratiosReturnOnEquity(),
    lang.ratiosReturnOnInvestedCapital(),
    lang.ratiosDividendPayoutRatio(),
    lang.ratiosDividendYield(),
    lang.ratiosEarningsPerShare(),
  ];

  const companyValues = company ? [
    company.grossProfitMargin?.toFixed(2) ?? emptyResult,
    company.operatingProfitMargin?.toFixed(2) ?? emptyResult,
    company.netProfitMargin?.toFixed(2) ?? emptyResult,
    company.netIncomePerShare?.toFixed(2) ?? emptyResult,
    company.bookValuePerShare?.toFixed(2) ?? emptyResult,
    company.returnOnAssets?.toFixed(2) ?? emptyResult,
    company.returnOnEquity?.toFixed(2) ?? emptyResult,
    company.roic?.toFixed(2) ?? emptyResult,
    company.dividendPayoutRatio ? company.dividendPayoutRatio.toFixed(2) : emptyResult,
    company.dividendYield?.toFixed(2) ?? emptyResult,
    company.eps ? company.eps.toFixed(2) : emptyResult,
  ] : [];

  const sectorValues = sector ? [
    sector.grossProfitMargin?.toFixed(2) ?? emptyResult,
    sector.operatingProfitMargin?.toFixed(2) ?? emptyResult,
    sector.netProfitMargin?.toFixed(2) ?? emptyResult,
    sector.netIncomePerShare?.toFixed(2) ?? emptyResult,
    sector.bookValuePerShare?.toFixed(2) ?? emptyResult,
    sector.returnOnAssets?.toFixed(2) ?? emptyResult,
    sector.returnOnEquity?.toFixed(2) ?? emptyResult,
    sector.roic?.toFixed(2) ?? emptyResult,
    sector.dividendPayoutRatio ? sector.dividendPayoutRatio.toFixed(2) : emptyResult,
    sector.dividendYield?.toFixed(2) ?? emptyResult,
    sector.eps ? sector.eps.toFixed(2) : emptyResult,
  ] : [];

  return [profitabilityNames, companyValues, sectorValues];
};

type liquidityProps = {
  liquidityStateData: LiquidityAndSolvencyRatiosResponse;
  liquidityPeriodType: RatiosPeriodType;
  setLiquidityTimeOptions: (timeOptions: string[]) => void;
  liquidityCurrentTimeOption: number;
}

export const getLiquidityData = (props: liquidityProps) => {
  const {
    liquidityStateData,
    liquidityPeriodType,
    setLiquidityTimeOptions,
    liquidityCurrentTimeOption,
  } = props;

  const isTtmPeriod = liquidityPeriodType === RatiosPeriodType.TTM;
  const isAnnualPeriod = liquidityPeriodType === RatiosPeriodType.ANNUAL;

  let timeOptions: string[] = [];
  let calendarYear: number | undefined;

  if (liquidityStateData.ratios) {
    if (isAnnualPeriod) {
      const ratiosPeriods = new Set<string>(
        liquidityStateData.ratios.map((ratio) => `${ratio.calendarYear}`),
      );

      let intersectedPeriods: string[];

      if (liquidityStateData.sectorRatios) {
        const sectorRatiosPeriods = new Set<string>(
          liquidityStateData.sectorRatios.map((sectorRatio) => `${sectorRatio.calendarYear}`),
        );

        intersectedPeriods = Array.from(ratiosPeriods).filter(period => sectorRatiosPeriods.has(period));
      } else {
        intersectedPeriods = Array.from(ratiosPeriods);
      }

      timeOptions = intersectedPeriods.map(period => getTimeOption({ calendarYear: parseInt(period, 10), period: '' }, liquidityPeriodType)).filter((item): item is string => item !== undefined);

      const currentRatio = liquidityStateData.ratios[liquidityCurrentTimeOption];
      calendarYear = currentRatio?.calendarYear;
    } else {
      timeOptions = liquidityStateData.ratios
        .map((ratio) => getTimeOption(ratio, liquidityPeriodType))
        .filter((item): item is string => item !== undefined);
    }
  }

  setLiquidityTimeOptions(timeOptions);

  const company = isTtmPeriod
    ? liquidityStateData.ttm
    : liquidityStateData.ratios && liquidityStateData.ratios[liquidityCurrentTimeOption];

  let sector: LiquidityAndSolvencyRatiosResponseElement | undefined;

  if (isTtmPeriod) {
    sector = liquidityStateData.sectorTtm;
  } else if (isAnnualPeriod) {
    sector = liquidityStateData.sectorRatios?.find(
      sectorRatio => sectorRatio.calendarYear === calendarYear,
    );
  } else if (liquidityStateData.sectorRatios) {
    sector = liquidityStateData.sectorRatios[liquidityCurrentTimeOption];
  }

  const liquidityNames = [
    lang.ratiosCurrentRatio(),
    lang.ratiosQuickRatio(),
    lang.ratiosCashRatio(),
    lang.ratiosDaysOfSales(),
    lang.ratiosDeptToEquity(),
    lang.ratiosInterestCoverageRatio(),
    lang.ratiosCashFlowToDebtRatio(),
  ];

  const companyValues: string[] = [];

  if (company) {
    companyValues.push(
      company.currentRatio?.toFixed(2) ?? emptyResult,
      company.quickRatio?.toFixed(2) ?? emptyResult,
      company.cashRatio?.toFixed(2) ?? emptyResult,
      company.daysOfSalesOutstanding?.toFixed(2) ?? emptyResult,
      company.debtEquityRatio?.toFixed(2) ?? emptyResult,
      company.interestCoverage?.toFixed(2) ?? emptyResult,
      company.cashFlowToDebtRatio?.toFixed(2) ?? emptyResult,
    );
  }

  const sectorValues = sector ? [
    sector.currentRatio?.toFixed(2) ?? emptyResult,
    sector.quickRatio?.toFixed(2) ?? emptyResult,
    sector.cashRatio?.toFixed(2) ?? emptyResult,
    sector.daysOfSalesOutstanding?.toFixed(2) ?? emptyResult,
    sector.debtEquityRatio?.toFixed(2) ?? emptyResult,
    sector.interestCoverage?.toFixed(2) ?? emptyResult,
    sector.cashFlowToDebtRatio?.toFixed(2) ?? emptyResult,
  ] : [];

  return [liquidityNames, companyValues, sectorValues];
};

type valuationProps = {
  valuationStateData: ValuationRatiosResponse;
  valuationPeriodType: RatiosPeriodType;
  setValuationTimeOptions: (timeOptions: string[]) => void;
  valuationCurrentTimeOption: number;
}

export const getValuationData = (props: valuationProps) => {
  const { valuationStateData, valuationPeriodType, setValuationTimeOptions, valuationCurrentTimeOption } = props;

  const isTtmPeriod = valuationPeriodType === RatiosPeriodType.TTM;
  const isAnnualPeriod = valuationPeriodType === RatiosPeriodType.ANNUAL;

  let timeOptions: string[] = [];
  let calendarYear: number | undefined;

  if (valuationStateData.ratios) {
    if (isAnnualPeriod) {
      const ratiosPeriods = new Set<string>(
        valuationStateData.ratios.map((ratio) => `${ratio.calendarYear}`),
      );

      let intersectedPeriods: string[];

      if (valuationStateData.sectorRatios) {
        const sectorRatiosPeriods = new Set<string>(
          valuationStateData.sectorRatios.map((sectorRatio) => `${sectorRatio.calendarYear}`),
        );

        intersectedPeriods = Array.from(ratiosPeriods).filter(period => sectorRatiosPeriods.has(period));
      } else {
        intersectedPeriods = Array.from(ratiosPeriods);
      }

      timeOptions = intersectedPeriods.map(period => {
        const [year] = period.split('-');
        return getTimeOption({ calendarYear: parseInt(year, 10) }, valuationPeriodType);
      }).filter((item): item is string => item !== undefined);

      const currentRatio = valuationStateData.ratios[valuationCurrentTimeOption];
      calendarYear = currentRatio?.calendarYear;
    } else {
      // Logic from the old function for non-annual periods
      timeOptions = valuationStateData.ratios
        .map((ratio) => getTimeOption(ratio, valuationPeriodType))
        .filter((item): item is string => item !== undefined);
    }
  }

  setValuationTimeOptions(timeOptions);

  const company = isTtmPeriod
    ? valuationStateData.ttm
    : valuationStateData.ratios && valuationStateData.ratios[valuationCurrentTimeOption];

  let sector: ValuationRatiosResponseElement | undefined;

  if (isTtmPeriod) {
    sector = valuationStateData.sectorTtm;
  } else if (isAnnualPeriod) {
    sector = valuationStateData.sectorRatios?.find(
      sectorRatio => sectorRatio.calendarYear === calendarYear,
    );
  } else if (valuationStateData.sectorRatios) {
    sector = valuationStateData.sectorRatios[valuationCurrentTimeOption];
  }

  const valuationNames = [
    lang.ratiosPriceToBookRatio(),
    lang.ratiosPriceToSalesRatio(),
    lang.ratiosPriceToEarningsRatio(),
    lang.ratiosPriceToFreeCashFlowRatio(),
    lang.ratiosEnterpriseValueToEbitda(),
    lang.ratiosEvToFreeCashFlow(),
    lang.ratiosPriceEarningsratio(),
  ];

  const companyValues = company ? [
    company.priceToBookRatio?.toFixed(2) ?? emptyResult,
    company.priceToSalesRatio?.toFixed(2) ?? emptyResult,
    company.priceEarningsRatio?.toFixed(2) ?? emptyResult,
    company.priceToFreeCashFlowsRatio?.toFixed(2) ?? emptyResult,
    company.enterpriseValueOverEbitda?.toFixed(2) ?? emptyResult,
    company.evToFreeCashFlow?.toFixed(2) ?? emptyResult,
    company.priceEarningsRatio?.toFixed(2) ?? emptyResult,
  ] : [];

  const sectorValues = sector ? [
    sector.priceToBookRatio?.toFixed(2) ?? emptyResult,
    sector.priceToSalesRatio?.toFixed(2) ?? emptyResult,
    sector.priceEarningsRatio?.toFixed(2) ?? emptyResult,
    sector.priceToFreeCashFlowsRatio?.toFixed(2) ?? emptyResult,
    sector.enterpriseValueOverEbitda?.toFixed(2) ?? emptyResult,
    sector.evToFreeCashFlow?.toFixed(2) ?? emptyResult,
    sector.priceEarningsRatio?.toFixed(2) ?? emptyResult,
  ] : [];

  return [valuationNames, companyValues, sectorValues];
};
